import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Scarecrow — a friendly field guardian who scares nobody: a patched mustard
// shirt stuffed with straw, straw hands and hair, a floppy hat, button eyes
// and a rosy smile — plus a little crow who has clearly decided they are
// friends, perched on one arm. Chunky toy proportions, about 1.3 m tall.
//
// Frame: faces +Z (where the face looks), planted on the floor (y = 0); the
// support post runs into the ground.
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "torso" : shirt, head, hat and hair — everything on the pole above the
//               waist. Pivot low on the post so it sways like a stick puppet.
//   - "arm-l" / "arm-r" : the sleeve arms with their straw hands. Pivot at
//               each shoulder so they flap.
//   - "crow"  : the little crow on the left arm. Animated with its own hops
//               so the flap looks like it is bouncing the bird.
// The post and the crossbar stub stay static (unnamed).

const SHIRT = "#d9a13c";       // warm mustard shirt
const SHIRT_DARK = "#b57f27";  // shaded sleeve underside
const PATCH_RED = "#cf5b4a";   // stitched patch
const PATCH_BLUE = "#5a8bbf";  // stitched patch
const HEAD = "#e8cf9a";        // burlap sack head
const STRAW = "#e5c25f";       // straw hair and hands
const HAT = "#8a6a3c";         // floppy felt hat
const HAT_DARK = "#6f5430";    // hat brim underside / band
const POST = "#7a5a38";        // wooden post
const PANTS = "#6f7d8c";       // patched blue-grey trousers
const COAL = "#2c2f36";        // button eyes, crow body
const CREAM = "#f6efe3";       // eye whites / stitching
const BEAK = "#ef8a2f";        // crow beak

// ── frame heights ────────────────────────────────────────────────────────────
const WAIST_Y = 0.52;          // torso pivots here (top of the trouser bundle)
const SHOULDER_Y = 0.92;       // arm hinge height
const SHOULDER_X = 0.14;       // arm hinge offset from the pole
const HEAD_CY = 1.08;          // head centre (r 0.15)
const CROW_X = -0.42;          // where the crow perches on the left arm
const CROW_Y = 1.0;            // crow root height (on top of the sleeve)

const TORSO_PIVOT: [number, number, number] = [0, WAIST_Y, 0];
const SHOULDER_L: [number, number, number] = [-SHOULDER_X, SHOULDER_Y, 0];
const SHOULDER_R: [number, number, number] = [SHOULDER_X, SHOULDER_Y, 0];
const CROW_PIVOT: [number, number, number] = [CROW_X, CROW_Y, 0];

// One sleeve arm reaching out along the crossbar, ending in a straw-tuft hand.
function arm(name: string, side: number): Part[] {
  const hx = side * 0.44; // hand tuft centre
  return [
    // Sleeve: a chunky cylinder lying along x, slightly drooping outward.
    { geometry: new THREE.CylinderGeometry(0.062, 0.072, 0.3, 10), color: SHIRT,
      position: [side * 0.27, SHOULDER_Y, 0], rotation: [0, 0, Math.PI / 2 + side * 0.12], name },
    // Cuff ring at the wrist.
    { geometry: new THREE.CylinderGeometry(0.066, 0.066, 0.035, 10), color: SHIRT_DARK,
      position: [side * 0.38, SHOULDER_Y - 0.015, 0], rotation: [0, 0, Math.PI / 2 + side * 0.12], name },
    // Straw hand: a splayed tuft of three straw spikes.
    { geometry: new THREE.ConeGeometry(0.035, 0.14, 6), color: STRAW,
      position: [hx, SHOULDER_Y - 0.02, 0], rotation: [0, 0, side * -1.35], name },
    { geometry: new THREE.ConeGeometry(0.025, 0.11, 5), color: STRAW,
      position: [hx - side * 0.01, SHOULDER_Y - 0.02, 0.04], rotation: [0.5, 0, side * -1.35], name },
    { geometry: new THREE.ConeGeometry(0.025, 0.11, 5), color: STRAW,
      position: [hx - side * 0.01, SHOULDER_Y - 0.02, -0.04], rotation: [-0.5, 0, side * -1.35], name },
  ];
}

// The little crow: a plump charcoal bird with a bright beak and cream eyes,
// perched on the left sleeve. All parts named "crow" so it hops as one.
function crow(): Part[] {
  const x = CROW_X, y = CROW_Y;
  return [
    // Plump body leaning forward.
    { geometry: new THREE.SphereGeometry(0.055, 12, 9), color: COAL, position: [x, y + 0.05, 0], scale: [0.9, 1, 1.15], name: "crow" },
    // Head.
    { geometry: new THREE.SphereGeometry(0.038, 10, 8), color: COAL, position: [x, y + 0.115, 0.035], name: "crow" },
    // Beak pointing +Z.
    { geometry: new THREE.ConeGeometry(0.016, 0.05, 6), color: BEAK, position: [x, y + 0.112, 0.08], rotation: [Math.PI / 2, 0, 0], name: "crow" },
    // Big friendly eyes.
    { geometry: new THREE.SphereGeometry(0.012, 6, 5), color: CREAM, position: [x - 0.022, y + 0.125, 0.06], name: "crow" },
    { geometry: new THREE.SphereGeometry(0.012, 6, 5), color: CREAM, position: [x + 0.022, y + 0.125, 0.06], name: "crow" },
    { geometry: new THREE.SphereGeometry(0.006, 6, 5), color: COAL, position: [x - 0.022, y + 0.126, 0.07], name: "crow" },
    { geometry: new THREE.SphereGeometry(0.006, 6, 5), color: COAL, position: [x + 0.022, y + 0.126, 0.07], name: "crow" },
    // Tail feathers.
    { geometry: new THREE.BoxGeometry(0.045, 0.015, 0.08), color: COAL, position: [x, y + 0.05, -0.08], rotation: [-0.35, 0, 0], name: "crow" },
  ];
}

function scarecrowParts(): Part[] {
  return [
    // ── post and crossbar (static) ──────────────────────────────────────────
    { geometry: new THREE.CylinderGeometry(0.035, 0.045, 0.56, 8), color: POST, position: [0, 0.28, 0] },
    { geometry: new THREE.BoxGeometry(0.06, 0.06, 0.06), color: POST, position: [0, WAIST_Y, 0] },

    // ── trousers: a patched bundle where the legs would be ─────────────────
    { geometry: new THREE.CylinderGeometry(0.1, 0.075, 0.26, 10), color: PANTS, position: [0, 0.56, 0], name: "torso" },
    { geometry: new THREE.BoxGeometry(0.07, 0.07, 0.015), color: PATCH_RED, position: [0.035, 0.55, 0.085], rotation: [0, 0.2, 0.2], name: "torso" },
    // Straw poking out of the trouser bottom.
    { geometry: new THREE.ConeGeometry(0.028, 0.1, 5), color: STRAW, position: [-0.035, 0.42, 0.02], rotation: [0.2, 0, 2.9], name: "torso" },
    { geometry: new THREE.ConeGeometry(0.028, 0.1, 5), color: STRAW, position: [0.035, 0.42, -0.02], rotation: [-0.2, 0, -2.9], name: "torso" },

    // ── shirt: a chunky stuffed torso ───────────────────────────────────────
    { geometry: new THREE.SphereGeometry(0.16, 14, 11), color: SHIRT, position: [0, 0.78, 0], scale: [1, 1.15, 0.85], name: "torso" },
    // Stitched patches.
    { geometry: new THREE.BoxGeometry(0.085, 0.085, 0.015), color: PATCH_BLUE, position: [-0.06, 0.78, 0.125], rotation: [0, -0.35, 0.25], name: "torso" },
    { geometry: new THREE.BoxGeometry(0.07, 0.07, 0.015), color: PATCH_RED, position: [0.07, 0.71, 0.105], rotation: [0, 0.45, -0.2], name: "torso" },
    // Rope belt at the waist.
    { geometry: new THREE.CylinderGeometry(0.105, 0.105, 0.035, 12), color: STRAW, position: [0, 0.665, 0], name: "torso" },

    // ── head: a burlap sack with a big friendly face ────────────────────────
    { geometry: new THREE.SphereGeometry(0.15, 16, 12), color: HEAD, position: [0, HEAD_CY, 0], scale: [1, 1.05, 0.95], name: "torso" },
    // Knot of sack tied at the neck.
    { geometry: new THREE.CylinderGeometry(0.055, 0.075, 0.07, 8), color: HEAD, position: [0, 0.945, 0], name: "torso" },
    // Button eyes: big cream discs with charcoal button centres.
    { geometry: new THREE.SphereGeometry(0.042, 10, 8), color: CREAM, position: [-0.058, 1.11, 0.115], name: "torso" },
    { geometry: new THREE.SphereGeometry(0.042, 10, 8), color: CREAM, position: [0.058, 1.11, 0.115], name: "torso" },
    { geometry: new THREE.SphereGeometry(0.024, 8, 6), color: COAL, position: [-0.055, 1.112, 0.15], name: "torso" },
    { geometry: new THREE.SphereGeometry(0.024, 8, 6), color: COAL, position: [0.055, 1.112, 0.15], name: "torso" },
    // Stitched smile: three little cream boxes in an upward arc.
    { geometry: new THREE.BoxGeometry(0.032, 0.014, 0.012), color: PATCH_RED, position: [-0.045, 1.035, 0.125], rotation: [0, 0.3, 0.3], name: "torso" },
    { geometry: new THREE.BoxGeometry(0.036, 0.014, 0.012), color: PATCH_RED, position: [0, 1.022, 0.135], name: "torso" },
    { geometry: new THREE.BoxGeometry(0.032, 0.014, 0.012), color: PATCH_RED, position: [0.045, 1.035, 0.125], rotation: [0, -0.3, -0.3], name: "torso" },
    // Triangle patch nose.
    { geometry: new THREE.ConeGeometry(0.024, 0.05, 4), color: PATCH_RED, position: [0, 1.07, 0.14], rotation: [Math.PI / 2, 0, 0], name: "torso" },
    // Straw hair fringing out under the hat.
    { geometry: new THREE.ConeGeometry(0.03, 0.11, 5), color: STRAW, position: [-0.12, 1.16, 0.03], rotation: [0.2, 0, 1.9], name: "torso" },
    { geometry: new THREE.ConeGeometry(0.03, 0.11, 5), color: STRAW, position: [0.12, 1.16, 0.03], rotation: [0.2, 0, -1.9], name: "torso" },
    { geometry: new THREE.ConeGeometry(0.026, 0.09, 5), color: STRAW, position: [-0.1, 1.15, -0.09], rotation: [-0.9, 0, 1.4], name: "torso" },
    { geometry: new THREE.ConeGeometry(0.026, 0.09, 5), color: STRAW, position: [0.1, 1.15, -0.09], rotation: [-0.9, 0, -1.4], name: "torso" },

    // ── floppy hat ──────────────────────────────────────────────────────────
    { geometry: new THREE.CylinderGeometry(0.2, 0.22, 0.035, 14), color: HAT, position: [0, 1.21, 0], rotation: [0.1, 0, 0.08], name: "torso" },
    { geometry: new THREE.ConeGeometry(0.13, 0.16, 12), color: HAT, position: [0, 1.29, -0.01], rotation: [0.1, 0, 0.08], name: "torso" },
    { geometry: new THREE.CylinderGeometry(0.115, 0.125, 0.035, 12), color: HAT_DARK, position: [0, 1.235, -0.005], rotation: [0.1, 0, 0.08], name: "torso" },

    // ── arms + crow ─────────────────────────────────────────────────────────
    ...arm("arm-l", -1),
    ...arm("arm-r", 1),
    ...crow(),
  ];
}

export const scarecrow = defineModel({
  id: "scarecrow",
  batchId: "mess",
  name: "Scarecrow",
  tags: ["mess", "cute", "animated", "character", "scarecrow", "farm", "crow", "mascot"],
  summary: "A friendly patched scarecrow with straw hands and hair, a floppy hat and a little crow friend on one arm.",
  description:
    "A storybook scarecrow who scares nobody: a patched mustard shirt stuffed " +
    "with straw, a burlap head with button eyes and a stitched smile, straw " +
    "hands and hair under a floppy hat — and a plump little crow perched " +
    "happily on one arm. The torso, both arms and the crow are separately " +
    "posable; it ships an idle sway and an arm flap that bounces the crow.",
  author: "models",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  parts: scarecrowParts,
  animations: [
    // 1. Idle — a gentle breeze sway: the torso rocks on the post, the arms
    //    droop and lift a touch, the crow bobs along with a tail flick.
    {
      id: "idle",
      label: "Idle sway",
      duration: 3.6,
      loop: true,
      tracks: [
        {
          target: "torso", pivot: TORSO_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0.04], ease: "sine" },
            { t: 1.8, value: [0.02, 0, -0.04], ease: "sine" },
            { t: 3.6, value: [0, 0, 0.04], ease: "sine" },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0.05], ease: "sine" },
            { t: 1.8, value: [0, 0, -0.06], ease: "sine" },
            { t: 3.6, value: [0, 0, 0.05], ease: "sine" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, -0.05], ease: "sine" },
            { t: 1.8, value: [0, 0, 0.06], ease: "sine" },
            { t: 3.6, value: [0, 0, -0.05], ease: "sine" },
          ],
        },
        // The crow bobs gently and flicks its tail.
        {
          target: "crow", pivot: CROW_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.8, value: [0, 0.012, 0], ease: "sine" },
            { t: 3.6, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.05, 0, 0], ease: "sine" },
            { t: 1.8, value: [-0.05, 0.15, 0], ease: "sine" },
            { t: 3.6, value: [0.05, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Flap — the scarecrow flaps both arms like it is shooing (or waving),
    //    and every flap bounces the little crow into a happy hop.
    {
      id: "flap",
      label: "Arm flap, crow hops",
      duration: 2.4,
      loop: true,
      tracks: [
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.4, value: [0, 0, -0.5], ease: "easeOut" },
            { t: 0.8, value: [0, 0, 0.15], ease: "easeIn" },
            { t: 1.2, value: [0, 0, -0.5], ease: "easeOut" },
            { t: 1.6, value: [0, 0, 0.15], ease: "easeIn" },
            { t: 2.4, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.4, value: [0, 0, 0.5], ease: "easeOut" },
            { t: 0.8, value: [0, 0, -0.15], ease: "easeIn" },
            { t: 1.2, value: [0, 0, 0.5], ease: "easeOut" },
            { t: 1.6, value: [0, 0, -0.15], ease: "easeIn" },
            { t: 2.4, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        // Each downbeat of the arm pops the crow into a little hop.
        {
          target: "crow", pivot: CROW_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.55, value: [0, 0.055, 0], ease: "easeOut" },
            { t: 0.8, value: [0, 0, 0], ease: "bounce" },
            { t: 1.35, value: [0, 0.055, 0], ease: "easeOut" },
            { t: 1.6, value: [0, 0, 0], ease: "bounce" },
            { t: 2.4, value: [0, 0, 0] },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.55, value: [-0.2, 0, 0.1], ease: "easeOut" },
            { t: 0.8, value: [0, 0, 0], ease: "easeIn" },
            { t: 1.35, value: [-0.2, 0, -0.1], ease: "easeOut" },
            { t: 1.6, value: [0, 0, 0], ease: "easeIn" },
            { t: 2.4, value: [0, 0, 0] },
          ],
        },
        // The torso leans back a touch with the effort.
        {
          target: "torso", pivot: TORSO_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.4, value: [-0.05, 0, 0], ease: "easeInOut" },
            { t: 0.8, value: [0.03, 0, 0], ease: "easeInOut" },
            { t: 1.2, value: [-0.05, 0, 0], ease: "easeInOut" },
            { t: 1.6, value: [0.03, 0, 0], ease: "easeInOut" },
            { t: 2.4, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
